import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { useTranslation } from 'react-i18next';

/**
 * Adding a fiscal identity without leaving what you were doing.
 *
 * Asking for a comprobante used to end at "add your fiscal data first", with
 * the form on another screen — a dead end at the exact moment someone had
 * decided they wanted an invoice. The same four fields, asked here.
 *
 * The régimen and the uso are chosen from the SAT's own lists rather than
 * typed: they are assertions to the tax authority, and a typo in either is a
 * comprobante the PAC refuses.
 */

interface Catalogue {
  code: string;
  label: string;
}

export interface FiscalProfile {
  id: string;
  label: string;
  taxId: string;
  legalName: string;
  taxRegime: string;
  fiscalPostalCode: string;
  cfdiUse: string | null;
  isDefault: boolean;
}

export default function FiscalProfileDialog({
  existing,
  onClose,
  onSaved,
}: {
  /** Editing one, or null to add another. */
  existing?: FiscalProfile | null;
  onClose: () => void;
  onSaved: (profile: FiscalProfile) => void;
}) {
  const { t } = useTranslation();
  const [regimes, setRegimes] = useState<Catalogue[]>([]);
  const [uses, setUses] = useState<Catalogue[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [label, setLabel] = useState(existing?.label ?? '');
  const [taxId, setTaxId] = useState(existing?.taxId ?? '');
  const [legalName, setLegalName] = useState(existing?.legalName ?? '');
  const [taxRegime, setTaxRegime] = useState(existing?.taxRegime ?? '');
  const [postalCode, setPostalCode] = useState(existing?.fiscalPostalCode ?? '');
  const [cfdiUse, setCfdiUse] = useState(existing?.cfdiUse ?? 'G03');

  useEffect(() => {
    (async () => {
      try {
        const catalogues = await apiFetch<{ taxRegimes: Catalogue[]; cfdiUses: Catalogue[] }>(
          '/customer/fiscal/catalogues'
        );
        setRegimes(catalogues.data?.taxRegimes || []);
        setUses(catalogues.data?.cfdiUses || []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body = JSON.stringify({
        label: label.trim() || 'Principal',
        taxId: taxId.trim().toUpperCase(),
        legalName: legalName.trim(),
        taxRegime,
        fiscalPostalCode: postalCode.trim(),
        ...(cfdiUse ? { cfdiUse } : {}),
      });

      const saved = existing
        ? await apiFetch<FiscalProfile>(`/customer/fiscal/profiles/${existing.id}`, {
            method: 'PUT',
            body,
          })
        : await apiFetch<FiscalProfile>('/customer/fiscal/profiles', { method: 'POST', body });

      onSaved(saved.data as FiscalProfile);
    } catch (err) {
      // The API's messages are written for the customer — an RFC that is not
      // one, a régimen that does not exist, an RFC they already added.
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const ready = taxId.trim() && legalName.trim() && taxRegime && /^\d{5}$/.test(postalCode.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {existing ? 'Editar datos fiscales' : 'Datos fiscales'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-500">
              Escríbelos igual que en tu constancia de situación fiscal. El SAT los valida juntos, y
              si uno no coincide la factura se rechaza.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fiscal.label')}
              </label>
              <input
                className="input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('fiscal.labelHint')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RFC</label>
                <input
                  className="input uppercase"
                  value={taxId}
                  onChange={(e) => setTaxId(e.target.value.toUpperCase())}
                  placeholder="XAXX010101000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fiscal.postalCode')}</label>
                <input
                  className="input"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  inputMode="numeric"
                  placeholder="64000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('fiscal.legalName')}</label>
              <input
                className="input"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder={t('fiscal.legalNameHint')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('fiscal.regime')}</label>
              <select className="input" value={taxRegime} onChange={(e) => setTaxRegime(e.target.value)}>
                <option value="">{t('fiscal.chooseRegime')}</option>
                {regimes.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.code} — {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('fiscal.cfdiUse')}</label>
              <select className="input" value={cfdiUse} onChange={(e) => setCfdiUse(e.target.value)}>
                {uses.map((u) => (
                  <option key={u.code} value={u.code}>
                    {u.code} — {u.label}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <p className="flex items-start gap-1.5 text-sm text-amber-700">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                {error}
              </p>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={save}
                disabled={!ready || saving}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                {t('fiscal.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
